import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import MediaPicker from "@/components/ui/media-picker";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Download,
  ExternalLink,
  Upload,
  Eye,
  X,
} from "lucide-react";

// Receipt Preview Dialog Component
const ReceiptPreviewDialog = ({ receiptUrl }: { receiptUrl: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isImageUrl = (url: string) => {
    return (
      /\.(jpg|jpeg|png|gif|webp)$/i.test(url) ||
      url.includes("supabase.co/storage")
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 text-blue-600 hover:text-blue-800"
        >
          <Eye className="h-3 w-3 mr-1" />
          Preview
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Receipt Preview
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(receiptUrl, "_blank")}
              className="text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Open Original
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {isImageUrl(receiptUrl) && !imageError ? (
            <div className="flex justify-center">
              <img
                src={receiptUrl}
                alt="Receipt"
                className="max-w-full max-h-[60vh] object-contain rounded-lg border"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="text-center py-8 space-y-4">
              <div className="text-muted-foreground">
                {imageError
                  ? "Failed to load image"
                  : "Preview not available for this file type"}
              </div>
              <div className="bg-muted/20 p-3 rounded-lg break-all text-sm">
                <strong>URL:</strong> {receiptUrl}
              </div>
              <Button
                onClick={() => window.open(receiptUrl, "_blank")}
                className="mt-4"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface PayoutActionDialogProps {
  payout: {
    id: number;
    amount: number;
    receipt_url?: string | null;
    notes?: string | null;
  };
  action: "complete" | "fail";
  isLoading: boolean;
  onAction: (
    payoutId: number,
    status: "completed" | "failed",
    notes?: string,
    receiptUrl?: string
  ) => Promise<void>;
  formatCurrency: (amount: number) => string;
}

const PayoutActionDialog = ({
  payout,
  action,
  isLoading,
  onAction,
  formatCurrency,
}: PayoutActionDialogProps) => {
  const [notes, setNotes] = useState(payout.notes || "");
  const [receiptUrl, setReceiptUrl] = useState(payout.receipt_url || "");
  const [isOpen, setIsOpen] = useState(false);

  // Add CSS to constrain MediaPicker dialog width when this dialog is open
  useEffect(() => {
    if (isOpen) {
      const style = document.createElement("style");
      style.id = "payout-dialog-media-picker-override";
      style.textContent = `
        .payout-action-dialog [data-radix-dialog-content] {
          max-width: min(480px, 90vw) !important;
        }
        .payout-action-dialog .grid-cols-3 {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
        .payout-action-dialog img {
          max-width: 100% !important;
          height: auto !important;
        }
        .payout-action-dialog .h-\\[200px\\] {
          height: 150px !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        const existingStyle = document.getElementById(
          "payout-dialog-media-picker-override"
        );
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, [isOpen]);

  const handleAction = async () => {
    // For failed payouts, notes are mandatory
    if (action === "fail" && !notes.trim()) {
      return; // The form validation will show the error
    }

    // For completed payouts, receipt is mandatory
    if (action === "complete" && !receiptUrl.trim()) {
      return; // The form validation will show the error
    }

    await onAction(
      payout.id,
      action === "complete" ? "completed" : "failed",
      notes.trim() || undefined,
      receiptUrl || undefined
    );
    setIsOpen(false);
  };

  const isComplete = action === "complete";
  const isFail = action === "fail";

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={
            isComplete
              ? "text-green-600 border-green-600 hover:bg-green-50/10"
              : "text-red-600 border-red-600 hover:bg-red-50/10"
          }
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isComplete ? (
            <CheckCircle className="h-3 w-3" />
          ) : (
            <XCircle className="h-3 w-3" />
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden payout-action-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isComplete ? "Mark Payout as Completed" : "Mark Payout as Failed"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isComplete
              ? `Mark this payout of ${formatCurrency(
                  payout.amount
                )} as completed. This will mark all associated earnings as "paid".`
              : `Mark this payout of ${formatCurrency(
                  payout.amount
                )} as failed. This will make all associated earnings available for future payout requests.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto max-h-[60vh]">
          {/* Receipt Upload - Only for completed payouts */}
          {isComplete && (
            <div className="space-y-2">
              <Label htmlFor="receipt">
                Receipt / Proof of Payment{" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="w-full max-w-full overflow-hidden">
                <div className="max-w-full">
                  <MediaPicker
                    onSelect={setReceiptUrl}
                    currentValue={receiptUrl}
                    allowedTypes={["image/jpeg", "image/jpg", "image/png"]}
                    title="Select Receipt"
                    description="Upload a receipt or proof of payment image, or provide an external URL"
                    buttonText="Select Receipt"
                    showPreview={true}
                  />
                </div>
              </div>
              {isComplete && !receiptUrl.trim() && (
                <p className="text-sm text-red-500">
                  Receipt is required when marking a payout as completed.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Upload an image of your receipt or proof of payment, or use an
                external URL
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes {isFail && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              id="notes"
              placeholder={
                isComplete
                  ? "Optional remarks or reference information..."
                  : "Please provide a reason for marking this payout as failed..."
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={isFail && !notes.trim() ? "border-red-500" : ""}
            />
            {isFail && !notes.trim() && (
              <p className="text-sm text-red-500">
                Notes are required when marking a payout as failed.
              </p>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAction}
            disabled={
              (isFail && !notes.trim()) || (isComplete && !receiptUrl.trim())
            }
            className={
              isComplete
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            }
          >
            {isComplete ? "Mark as Completed" : "Mark as Failed"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export { ReceiptPreviewDialog };
export default PayoutActionDialog;
